<template>
  <span v-if="available">
    <v-btn color="primary" @click="open()">
      <v-icon left>sync</v-icon>Sync with NMS
    </v-btn>

    <v-dialog v-model="dialog" max-width="1100" persistent>
      <v-card>
        <v-card-title>
          <h3>Sync {{ networkName }} with the NMS</h3>
        </v-card-title>
        <v-card-text>
          <p>
            Reads the network management system and shows what would change on this network's devices. Nothing is saved until
            you click Apply. Only ONT records that match exactly one NMS ONU (by serial, else by unit) are updated: GPON serial
            (the old serial is kept as Product Number), OLT, PON port, label if empty, and online status. OLT records are created
            if missing. ONTs are never created or deleted, and service accounts and the network itself are not touched.
          </p>

          <v-alert :value="!!message" :type="messageType" outline>{{ message }}</v-alert>

          <div v-if="plan">
            <p class="mt-3">
              NMS: <strong>{{ plan.nms.olts }}</strong> OLTs, <strong>{{ plan.nms.onus }}</strong> ONUs.
              This network: <strong>{{ s.ontRecords }}</strong> ONT records, <strong>{{ s.matched }}</strong> matched
              ({{ s.bySerial }} by serial, {{ s.byUnit }} by unit), <strong>{{ s.toUpdate }}</strong> to update<span v-if="s.statusOnly">
              ({{ s.statusOnly }} online status only)</span>, {{ s.unchanged }} already up to date,
              <span :class="s.notMatched ? 'orange--text' : ''">{{ s.notMatched }} not matched</span>.
              <span v-if="s.oltsToCreate"><strong>{{ s.oltsToCreate }}</strong> OLT records will be created.</span>
              <span v-if="s.placeholders">{{ s.placeholders }} NMS ONUs are at units with no ONT record (left as they are).</span>
            </p>

            <div v-if="plan.olts.length">
              <h4 class="mt-3">OLTs</h4>
              <v-data-table :headers="oltHeaders" :items="plan.olts" hide-actions class="elevation-1">
                <template v-slot:items="props">
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.oltId }}</td>
                  <td>{{ props.item.onus }}</td>
                  <td>{{ props.item.action === 'create' ? 'Will be created' : 'Exists (device ' + props.item.deviceId + ')' }}</td>
                </template>
              </v-data-table>
            </div>

            <h4 class="mt-3">Changes ({{ plan.changes.length }})</h4>
            <v-data-table :headers="changeHeaders" :items="plan.changes" :rows-per-page-items="[10, 50, 500]" class="elevation-1">
              <template v-slot:items="props">
                <td>{{ props.item.unit }}</td>
                <td>{{ props.item.how }}</td>
                <td>{{ props.item.nmsSerial }}</td>
                <td>{{ props.item.olt }} {{ props.item.pon }}</td>
                <td>
                  <div v-for="(v, f) in props.item.fields" :key="f" class="grey--text text--darken-2">
                    {{ label(f) }}: {{ show(f, v[0]) }} &rarr; {{ show(f, v[1]) }}
                  </div>
                </td>
              </template>
            </v-data-table>

            <div v-if="plan.problems.length">
              <h4 class="mt-3 orange--text">Not matched ({{ plan.problems.length }}) - left unchanged</h4>
              <v-data-table :headers="problemHeaders" :items="plan.problems" :rows-per-page-items="[10, 50, 500]" class="elevation-1">
                <template v-slot:items="props">
                  <td>{{ props.item.unit }}</td>
                  <td>{{ props.item.serial }}</td>
                  <td>{{ props.item.label }}</td>
                  <td>{{ props.item.reason }}</td>
                </template>
              </v-data-table>
            </div>

            <div v-if="plan.placeholders.length">
              <h4 class="mt-3">NMS ONUs at units with no ONT record ({{ plan.placeholders.length }}) - not created</h4>
              <v-data-table :headers="placeholderHeaders" :items="plan.placeholders" :rows-per-page-items="[10, 50, 500]" class="elevation-1">
                <template v-slot:items="props">
                  <td>{{ props.item.unit }}</td>
                  <td>{{ props.item.accounts }}</td>
                  <td>{{ props.item.serial }}</td>
                  <td>{{ props.item.olt }} {{ props.item.pon }}</td>
                  <td>{{ props.item.status }}</td>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="close()">Close</v-btn>
          <v-btn flat color="primary" :loading="loading" :disabled="applying" @click="preview()">Refresh preview</v-btn>
          <v-btn color="primary" :loading="applying" :disabled="!canApply" @click="apply()">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import Api from "@/services/Api";

const LABELS = {
  serialNumber: "Serial", productNumber: "Product number", upstreamDeviceId: "OLT", slot: "Slot", port: "PON port",
  gponOnuId: "ONU id", note: "Label", isOnline: "Online"
};

export default {
  props: ["locationNetworkId", "networkName"],
  data() {
    return {
      available: false,
      dialog: false,
      loading: false,
      applying: false,
      applied: false,
      plan: null,
      message: "",
      messageType: "info",
      oltHeaders: [
        { text: "OLT", value: "name", sortable: false },
        { text: "IP", value: "oltId", sortable: false },
        { text: "ONUs on NMS", value: "onus", sortable: false },
        { text: "Portal record", value: "action", sortable: false }
      ],
      changeHeaders: [
        { text: "Unit", value: "unit" },
        { text: "Matched by", value: "how" },
        { text: "NMS serial", value: "nmsSerial" },
        { text: "OLT / PON", value: "olt" },
        { text: "Changes", value: "fields", sortable: false }
      ],
      problemHeaders: [
        { text: "Unit", value: "unit" },
        { text: "Portal serial", value: "serial" },
        { text: "Label", value: "label" },
        { text: "Why", value: "reason" }
      ],
      placeholderHeaders: [
        { text: "Unit", value: "unit" },
        { text: "Live accounts", value: "accounts" },
        { text: "NMS serial", value: "serial" },
        { text: "OLT / PON", value: "olt" },
        { text: "Status", value: "status" }
      ]
    };
  },
  computed: {
    s() {
      return this.plan ? this.plan.summary : {};
    },
    canApply() {
      return this.plan && !this.loading && !this.applying && !this.applied && this.plan.changes.length > 0;
    }
  },
  watch: {
    locationNetworkId() {
      this.checkAvailable();
    }
  },
  mounted() {
    this.checkAvailable();
  },
  methods: {
    async checkAvailable() {
      this.available = false;
      if (!this.locationNetworkId) return;
      try {
        const response = await Api().get("/nmsSync/" + this.locationNetworkId + "/status");
        this.available = !!response.data.available;
      } catch (e) {
        this.available = false;
      }
    },
    label(f) {
      return LABELS[f] || f;
    },
    show(f, v) {
      if (v === null || v === undefined || v === "") return "-";
      if (f === "isOnline") return Number(v) ? "yes" : "no";
      if (f === "upstreamDeviceId" && String(v).indexOf("new:") === 0) return "new OLT record " + String(v).slice(4);
      return v;
    },
    open() {
      this.dialog = true;
      this.applied = false;
      this.preview();
    },
    close() {
      this.dialog = false;
      if (this.applied) this.$emit("synced");
    },
    async preview() {
      this.loading = true;
      this.message = "";
      this.applied = false;
      try {
        const response = await Api().get("/nmsSync/" + this.locationNetworkId + "/preview");
        this.plan = response.data;
        this.messageType = "info";
        this.message = this.plan.changes.length
          ? "Check the list, then click Apply."
          : "Nothing to change: every matched ONT is already up to date.";
      } catch (e) {
        this.plan = null;
        this.messageType = "error";
        this.message = (e.response && e.response.data && e.response.data.message) || "Could not read the NMS.";
      }
      this.loading = false;
    },
    async apply() {
      if (!confirm("Update " + this.plan.changes.length + " ONT records" +
        (this.s.oltsToCreate ? " and create " + this.s.oltsToCreate + " OLT records" : "") + " in " + this.networkName + "?")) return;
      this.applying = true;
      this.message = "";
      try {
        const response = await Api().post("/nmsSync/" + this.locationNetworkId + "/apply", {});
        const d = response.data.done;
        this.applied = true;
        this.messageType = "success";
        this.message = "Saved: " + d.ontsUpdated + " ONT records updated" +
          (d.oltsCreated.length ? ", OLT records created: " + d.oltsCreated.map(o => o.name).join(", ") : "") + ".";
      } catch (e) {
        this.messageType = "error";
        this.message = (e.response && e.response.data && e.response.data.message) || "The sync failed; nothing was saved.";
      }
      this.applying = false;
    }
  }
};
</script>
